import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- Enum定義 ---

export const ContractStatus = {
  ACTIVE: '契約中',
  TERMINATED: '契約終了',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ContractStatus = keyof typeof ContractStatus;

// --- 型定義 ---

/** current/pending構造（汎用） */
export type CurrentPendingValue<T> = {
  current: T;
  pending: T | null;
};

/** ID+名前の参照オブジェクト */
export type IdNamePair = {
  id: string;
  name: string;
};

/** ユーザー参照オブジェクト */
export type UserRef = {
  userId: string;
  userName: string;
};

/** プロジェクト参照オブジェクト */
export type ProjectRef = {
  projectId: string;
  projectName: string;
};

/** 契約項目DTO */
export type ContractItemDto = {
  currentValues: string[];
  pendingValues: string[] | null;
};

/** 契約取得APIレスポンス */
export type ContractDetailResult = {
  contractId: string;
  contractSubId: string;
  managementId: string;
  status: ContractStatus;
  prevContractSubId: string | null;
  nextContractSubId: string | null;
  hasPendingRequest: boolean;
  personInCharge: CurrentPendingValue<UserRef>;
  project: ProjectRef;
  manager: UserRef;
  client: { clientId: string; clientName: string };
  member: { memberId: string; memberName: string };
  partner: { partnerId: string; partnerName: string };
  prefecture: CurrentPendingValue<IdNamePair>;
  location: CurrentPendingValue<string | null>;
  contractType: CurrentPendingValue<IdNamePair>;
  contractStartDate: CurrentPendingValue<string>;
  contractEndDate: CurrentPendingValue<string>;
  costAmount: CurrentPendingValue<number>;
  salesAmount: CurrentPendingValue<number>;
  operationRate: CurrentPendingValue<number>;
  contractUnit: CurrentPendingValue<IdNamePair>;
  adjustmentUpperLimit: CurrentPendingValue<number | null>;
  adjustmentLowerLimit: CurrentPendingValue<number | null>;
  needsOriginSheet: CurrentPendingValue<boolean>;
  originSheetDestination: CurrentPendingValue<string | null>;
  remarks: CurrentPendingValue<string | null>;
  contractItems: Record<string, ContractItemDto>;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
};

/** 契約一覧APIレスポンスの1件 */
export type ContractListResult = {
  contractId: string;
  contractSubId: string;
  managementId: string;
  status: ContractStatus;
  hasPendingRequest: boolean;
  personInChargeId: string;
  personInChargeName: string;
  projectId: string;
  projectName: string;
  managerId: string;
  managerName: string;
  clientId: string;
  clientName: string;
  memberId: string;
  memberName: string;
  partnerId: string;
  partnerName: string;
  contractTypeId: string;
  contractTypeName: string;
  contractStartDate: string;
  contractEndDate: string;
  costAmount: number;
  salesAmount: number;
  operationRate: number;
  contractUnitId: string;
  contractUnitName: string;
  createdAt: string;
  updatedAt: string;
};

/** 契約取得APIリクエスト */
export type GetContractParams = {
  contractId?: string;
  contractSubId?: string;
};

/** 契約一覧取得APIリクエスト */
export type ListContractParams = {
  projectId?: string;
  memberId?: string;
  partnerId?: string;
  clientId?: string;
  managerId?: string;
  personInChargeId?: string;
  contractTypeId?: string;
  fiscalYear?: number;
  fiscalMonth?: number;
  managementId?: string;
  includeTerminated?: boolean;
};

// --- API関数 ---

/**
 * 契約取得API (API3401)
 * @param params.contractId - 契約ID（任意, 最大64文字, contractIdまたはcontractSubIdのいずれかを指定）
 * @param params.contractSubId - 契約サブID（任意, 最大64文字, contractIdまたはcontractSubIdのいずれかを指定）
 */
export const getContract = async (params: GetContractParams): Promise<ContractDetailResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_CONTRACT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 契約一覧取得API (API3402)
 * @param params.projectId - プロジェクトID（任意, 最大64文字, 完全一致）
 * @param params.memberId - 要員ID（任意, 最大64文字, 完全一致）
 * @param params.partnerId - パートナーID（任意, 最大64文字, 完全一致）
 * @param params.clientId - 取引先ID（任意, 最大64文字, 完全一致）
 * @param params.managerId - 課長ID（任意, 最大300文字, 完全一致）
 * @param params.personInChargeId - 担当者ID（任意, 最大300文字, 完全一致）
 * @param params.contractTypeId - 契約形態ID（任意, 最大300文字, 完全一致）
 * @param params.fiscalYear - 年度（任意, 契約期間が指定年度に含まれる）
 * @param params.fiscalMonth - 年度月（任意, 契約期間が指定年度月に含まれる）
 * @param params.managementId - 管理ID（任意, 最大50文字, 完全一致）
 * @param params.includeTerminated - 契約終了を含む（任意, デフォルト: false）
 */
export const listContract = async (params: ListContractParams): Promise<ContractListResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_CONTRACT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
